import cn from 'classnames';
import { ReactNode } from 'react';

export type NavPillsItem<T extends string> = {
  value: T;
  label: string;
  disabled?: boolean;
  icon?: ReactNode;
};

export type NavPillsRenderItemParams<T extends string> = {
  item: NavPillsItem<T>;
  isActive: boolean;
  itemClassName?: string;
};

type NavPillsProps<T extends string> = {
  items: NavPillsItem<T>[];
  activeItem: T;
  onItemClick: (item: T) => void;
  className?: string;
  activeItemClassName?: string;
  getItemClassName?: (params: NavPillsRenderItemParams<T>) => string | undefined;
  direction?: 'horizontal' | 'vertical';
  renderItem?: (params: NavPillsRenderItemParams<T>) => ReactNode;
};

export const NavPills = <T extends string>({
  items,
  activeItem,
  onItemClick,
  className,
  activeItemClassName,
  getItemClassName,
  direction = 'horizontal',
  renderItem
}: NavPillsProps<T>) => (
  <ul className={cn('nav nav-pills fw-semibold', { 'flex-column': direction === 'vertical' }, className)}>
    {items.map((item) => {
      const { value, disabled } = item;
      const isActive = activeItem === value;
      const itemClassName = getItemClassName?.({ item, isActive });

      return (
        <li key={value} className="nav-item">
          <a
            href="#"
            className={cn(
              'nav-link px-5 py-3',
              {
                'bg-light-primary text-primary': isActive && !disabled && !activeItemClassName,
                'text-gray-400': !isActive && !disabled,
                'text-gray-350': disabled
              },
              isActive && !disabled && activeItemClassName,
              itemClassName
            )}
            aria-current={isActive ? 'page' : undefined}
            onClick={(e) => {
              e.preventDefault();
              if (!disabled) onItemClick(value);
            }}
          >
            {renderItem ? (
              renderItem({ item, isActive, itemClassName })
            ) : (
              <>
                {item.icon && <span className="me-2">{item.icon}</span>}
                {item.label}
              </>
            )}
          </a>
        </li>
      );
    })}
  </ul>
);
